import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, forkJoin, of, timer } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  takeUntil,
  timeout,
} from 'rxjs/operators';

import { patientQuery } from '@patient-ui/patient-web/store';
import {
  ORBI_APPOINTMENT_SCHEDULE_NAVIGATION_DELAY_MS,
  ORBI_RETURN_TYPE,
  SERVICE_TYPES,
  isReturnType,
} from '@patient-ui/shared/constants';
import { OrbiMessageType } from '@patient-ui/shared/enums';
import {
  IAddress,
  OrbiFindLabData,
  OrbiMessage,
} from '@patient-ui/shared/models';
import {
  mapOrbiFindLabData,
  orbiMessageLocksChat,
} from '@patient-ui/shared/utils';

import {
  OrbiFindLabHandoff,
  OrbiFindLabHandoffService,
  matchOrbiInitialService,
} from './orbi-find-lab-handoff.service';
import {
  AvailableService,
  AvailableServicesService,
} from '../../portal-appointments/services/available-services.service';

interface OrbiScheduleLookups {
  services: AvailableService[];
  profileAddress: string;
}

/**
 * Root-scoped because completing the hand-off closes the panel, which destroys
 * the thread that started it.
 */
@Injectable({ providedIn: 'root' })
export class OrbiAppointmentScheduleNavigationService {
  /** A stuck lookup counts as empty rather than holding the hand-off. */
  private static readonly LOOKUP_TIMEOUT_MS = 6000;

  private readonly handoffs = new Subject<OrbiFindLabData>();
  private readonly cancels = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly availableServices: AvailableServicesService,
    private readonly findLab: OrbiFindLabHandoffService
  ) {
    // `switchMap`: a newer answer replaces one still counting down.
    this.handoffs
      .pipe(
        switchMap((data) =>
          forkJoin([
            timer(ORBI_APPOINTMENT_SCHEDULE_NAVIGATION_DELAY_MS),
            this.lookups(),
          ]).pipe(
            map(([, lookups]) => ({ data, lookups })),
            takeUntil(this.cancels)
          )
        )
      )
      .subscribe(({ data, lookups }) => this.navigate(data, lookups));
  }

  handleAnsweredTurn(message: OrbiMessage): boolean {
    // A locking guardrail keeps the user on its safety message, as on mobile.
    if (
      message.type !== OrbiMessageType.Llm ||
      message.agent?.trim().toLowerCase() !== 'appointments' ||
      !isReturnType(message.returnType, ORBI_RETURN_TYPE.AppointmentSchedule) ||
      orbiMessageLocksChat(message.guardrailTriggered, message.guardrailType)
    ) {
      return false;
    }
    this.handoffs.next(mapOrbiFindLabData(message.structuredData?.raw));
    return true;
  }

  cancel(): void {
    this.cancels.next();
  }

  private lookups(): Observable<OrbiScheduleLookups> {
    const services = this.availableServices.getUserVisibleServices().pipe(
      take(1),
      timeout(OrbiAppointmentScheduleNavigationService.LOOKUP_TIMEOUT_MS),
      catchError(() => of([] as AvailableService[]))
    );
    const profileAddress = this.store
      .select(patientQuery.selectPrimaryAccountHolder)
      .pipe(
        take(1),
        map((patient) => formatProfileAddress(patient?.addresses)),
        catchError(() => of(''))
      );
    return forkJoin([services, profileAddress]).pipe(
      map(([resolvedServices, address]) => ({
        services: resolvedServices,
        profileAddress: address,
      }))
    );
  }

  private navigate(data: OrbiFindLabData, lookups: OrbiScheduleLookups): void {
    const service = resolveScheduleService(
      data.initialService,
      lookups.services
    );
    const search: OrbiFindLabHandoff = {
      address: data.initialLocation ?? lookups.profileAddress,
      serviceId: service.serviceId,
      serviceName: service.serviceName,
      showUser: service.showUser,
    };

    if (
      data.initialSearchLatitude !== undefined &&
      data.initialSearchLongitude !== undefined
    ) {
      search.latitude = data.initialSearchLatitude;
      search.longitude = data.initialSearchLongitude;
    }
    if (data.initialDate) {
      search.date = data.initialDate;
    }
    this.findLab.openResults(search);
  }
}

const KNOWN_SERVICES: AvailableService[] = Object.values(SERVICE_TYPES).map(
  ({ id, name }) => ({ serviceId: id, serviceName: name, showUser: true })
);

const LABWORK: AvailableService = {
  serviceId: SERVICE_TYPES['LABWORK'].id,
  serviceName: SERVICE_TYPES['LABWORK'].name,
  showUser: true,
};

/**
 * Always yields a service, so the results page shows a Service Type and can
 * load slots. The live catalog decides when it loaded; the portal's fixed
 * service types stand in when it failed. Anything unresolved falls back to
 * Labwork, the service the results page searches by default.
 */
function resolveScheduleService(
  initialService: string | undefined,
  catalog: readonly AvailableService[]
): AvailableService {
  const matched =
    matchOrbiInitialService(initialService, catalog) ??
    (catalog.length
      ? null
      : matchOrbiInitialService(initialService, KNOWN_SERVICES));
  if (matched) {
    return matched;
  }
  const raw = initialService?.trim() ?? '';
  if (/^\d+$/.test(raw)) {
    // Numeric ids pass through even without a catalog match, as on mobile.
    const id = Number(raw);
    return {
      serviceId: id,
      serviceName:
        KNOWN_SERVICES.find((s) => s.serviceId === id)?.serviceName ?? '',
      showUser: true,
    };
  }
  return catalog.find((s) => s.serviceId === LABWORK.serviceId) ?? LABWORK;
}

/** Primary-or-first address as `LINE1, LINE2, CITY, ST ZIP`, upper-cased. */
function formatProfileAddress(addresses: IAddress[] | undefined): string {
  if (!addresses?.length) {
    return '';
  }
  const address = addresses.find((a) => a.isPrimary) ?? addresses[0];
  return [
    address.line1,
    address.line2,
    address.city,
    `${address.state} ${address.zipCode}`,
  ]
    .filter((part) => !!part?.trim())
    .join(', ')
    .toUpperCase();
}
